//
// Copyright (c) 2011-2020 by Curalate, Inc.
//
import React, { useMemo } from 'react';
import styled from 'styled-components';

import { useDateRangePickerContext } from '@components/date-range-picker/DateRangePickerContext';
import DateString from '@models/DateString';
import { isDefined } from '@utils/filterPredicates';
import { DAYS_OF_WEEK_ABBR } from '@utils/moment';

const DAY_CELL_SIZE = 32;
export const CALENDAR_MONTH_WIDTH = 32 * 7;
function getOffsetFromSunday(date: DateString) {
  const weekday = DateString.getDayOfWeek(date);
  return DAYS_OF_WEEK_ABBR.indexOf(weekday);
}

/**
 * Check whether a number is within the ranged defined by 2 bounds, inclusive.
 * @param x Value to check
 * @param bounds Bounds to check against, in any order
 * @returns true if the number is within the bounds, false otherwise.
 */
const isWithin = (x: number, bounds: [number, number]): boolean => {
  const [y, z] = bounds;
  // Bounds can be in either order
  return y > z ? x <= y && x >= z : x >= y && x <= z;
};

/**
 * Get the number of days the target day is from the base date. Result will be memoized.
 * @param baseDate Date to calculate the offset from.
 * @param targetDate Date to calculate the offset to.
 * @returns number of days offset. Positive if targetDate is after baseDate. Negative if targetDate is before baseDate. Zero if they are the same.
 */
const useMemoizedDayOffset = (
  baseDate: DateString,
  targetDate?: DateString,
): number | undefined =>
  useMemo(
    () =>
      targetDate ? DateString.getDayOffset(baseDate, targetDate) : undefined,
    [baseDate, targetDate],
  );

const CalendarMonth = ({
  monthStartDate,
}: {
  monthStartDate: DateString;
}): JSX.Element => {
  const {
    minDate,
    maxDate,
    dateClicked,
    dateHovered,
    dateUnhovered,
    startDate,
    endDate,
    candidate,
  } = useDateRangePickerContext();

  // How many days do we skip before we start rendering dates?
  const skipDays = getOffsetFromSunday(monthStartDate);
  // How many days in this month?
  const monthDays = DateString.getDaysInMonth(monthStartDate);
  // Is this particular day offset in this month selected?

  const selectionStartOffset = useMemoizedDayOffset(monthStartDate, startDate);
  const selectionEndOffset = useMemoizedDayOffset(monthStartDate, endDate);
  const candidateOffset = useMemoizedDayOffset(monthStartDate, candidate);
  const minDateOffset = useMemoizedDayOffset(monthStartDate, minDate);
  const maxDateOffset = useMemoizedDayOffset(monthStartDate, maxDate);

  const isDateSelected = (idx: number): boolean => {
    if (isDefined(selectionStartOffset) && isDefined(selectionEndOffset)) {
      // Full range selected: highlight dates in range.
      return idx >= selectionStartOffset && idx <= selectionEndOffset;
    } else if (isDefined(selectionStartOffset) && isDefined(candidateOffset)) {
      // Selecting 2nd bound: highlight dates in range up to candidate bound.
      return isWithin(idx, [candidateOffset, selectionStartOffset]);
    }
    return false;
  };

  const isDateBounds = (idx: number): boolean => {
    if (isDefined(selectionStartOffset) && isDefined(selectionEndOffset)) {
      // Selected state: Highlight each bound, or hovered dates outside of bounds. (new candidate bound)
      return (
        idx === selectionStartOffset ||
        idx === selectionEndOffset ||
        (!isDateSelected(idx) && idx === candidateOffset)
      );
    } else if (isDefined(selectionStartOffset)) {
      // Selecting 2nd bound state: Highlight first bound.
      return idx === selectionStartOffset;
    }
    // Initial "No Date" state: Highlight hovered date.
    return idx === candidateOffset;
  };
  const isDateDisabled = (idx: number): boolean => {
    return (
      (isDefined(minDateOffset) && idx < minDateOffset) ||
      (isDefined(maxDateOffset) && idx > maxDateOffset)
    );
  };

  const monthText = useMemo(
    () => DateString.format(monthStartDate, 'MMMM YYYY'),
    [monthStartDate],
  );

  return (
    <div>
      <MonthTitle>{monthText}</MonthTitle>
      <CalendarGrid>
        <Cell>S</Cell>
        <Cell>M</Cell>
        <Cell>T</Cell>
        <Cell>W</Cell>
        <Cell>T</Cell>
        <Cell>F</Cell>
        <Cell>S</Cell>
        {new Array(skipDays).fill(0).map((_, idx) => (
          <Cell key={`blank_${idx}`} />
        ))}
        {new Array(monthDays).fill(0).map((_, idx) => {
          const isDisabled = isDateDisabled(idx);
          const evtHandlerProps = isDisabled
            ? {}
            : {
                onClick: () =>
                  dateClicked(DateString.addDays(monthStartDate, idx)),
                onMouseEnter: () =>
                  dateHovered(DateString.addDays(monthStartDate, idx)),
                onMouseLeave: () =>
                  dateUnhovered(DateString.addDays(monthStartDate, idx)),
              };
          return (
            <Cell
              isSelected={isDateSelected(idx)}
              isSelectedBound={isDateBounds(idx)}
              isDisabled={isDisabled}
              key={idx + 1}
              {...evtHandlerProps}
            >
              {idx + 1}
            </Cell>
          );
        })}
      </CalendarGrid>
    </div>
  );
};

const MonthTitle = styled.div(
  ({ theme }) => `
  font-weight: ${theme.fontWeights.semiBold};
  text-align: center;
  padding-bottom: ${theme.spacings[4]};
`,
);

const CalendarGrid = styled.div(
  ({ theme }) => `
  display: inline-grid;
  grid-template-columns: repeat(7,1fr);
  width: ${DAY_CELL_SIZE * 7}px;
`,
);

const Cell = styled.div<{
  isSelected?: boolean;
  isSelectedBound?: boolean;
  isDisabled?: boolean;
}>(
  ({
    isSelected = false,
    isSelectedBound = false,
    isDisabled = false,
    theme,
  }) => `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${DAY_CELL_SIZE}px;
  height: ${DAY_CELL_SIZE}px;
  cursor: default;
  ${(() => {
    if (isDisabled) {
      return `
        color: ${theme.colors.aliases.disabled};
        background-color: ${theme.colors.gray00};
      `;
    } else if (isSelectedBound) {
      return `
      background-color: ${theme.colors.aliases.primaryShade2};
      color: ${theme.colors.white};
    `;
    } else if (isSelected) {
      return `
        background-color: ${theme.colors.aliases.primary};
        color: ${theme.colors.white};
      `;
    }
    return '';
  })()}
`,
);

export default CalendarMonth;
